import { baseGatewayApiV1 } from '@verantos-dev/app-state-base';
import { CONCEPT_GROUPS_BASE_PATH } from '@verantos-dev/core-constants';
import { ObjectResponse, type ListResponse } from '@verantos-dev/types-common';
import type {
  ConceptGroup,
  LegacyConceptGroup,
  IsValidCreateConceptGroupNameResponse,
  IsValidCreateUploadResponse,
  CreateConceptGroupResponse,
  ConceptGroupConcept,
  ConceptGroupPreviewConfirmRequest,
  OmopConcept,
} from '@verantos-dev/types-concepts';

const CONCEPT_GROUP_TAG = 'concept-group';
const CUSTOM_CHARACTERISTICS_CONCEPT_GROUP_TAG = 'custom-characteristics-concept-groups';
/**
 * apiSlice.ts
 *
 */
// inject endpoints for feature into the baseApi
export const conceptGroupsApiSlice = baseGatewayApiV1
  .enhanceEndpoints({
    addTagTypes: [CONCEPT_GROUP_TAG, CUSTOM_CHARACTERISTICS_CONCEPT_GROUP_TAG],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      // Queries: for more information see: https://redux-toolkit.js.org/rtk-query/usage/queries
      // getter endpoint
      getLegacyConceptGroups: builder.query<ListResponse<LegacyConceptGroup>, string>({
        // define the endpoint for the query
        query: (projectId: string) => `/${projectId}/concept-groups`,
      }),
      getConceptGroups: builder.query<ListResponse<ConceptGroup>, { urlParams?: string }>({
        // define the endpoint for the query
        query: ({ urlParams }) => {
          const params = urlParams ? `${encodeURI(urlParams)}` : '';
          return `/concept-groups?${params}`;
        },
        providesTags: [CONCEPT_GROUP_TAG],
      }),
      getConceptGroupsForCustomCharacteristics: builder.query<
        ListResponse<ConceptGroup>,
        { urlParams?: string; cohortProfileId?: string; domain?: string }
      >({
        // define the endpoint for the query
        query: ({ urlParams, cohortProfileId, domain }) => {
          let params = urlParams ? `${encodeURI(urlParams)}` : '';

          // Add cohortProfileId and domain if provided
          if (cohortProfileId) {
            params += params ? '&' : '';
            params += `cohortProfileId=${encodeURIComponent(cohortProfileId)}`;
          }
          if (domain) {
            params += params ? '&' : '';
            params += `domain=${encodeURIComponent(domain)}`;
          }

          return `/concept-groups?${params}`;
        },
        // Use different tag to avoid caching conflicts
        providesTags: [CUSTOM_CHARACTERISTICS_CONCEPT_GROUP_TAG],
      }),
      getConceptGroupConceptsDetails: builder.query<
        ListResponse<ConceptGroupConcept>,
        { conceptGroupId: string; searchParams?: string }
      >({
        query: ({ conceptGroupId, searchParams }) => {
          return `/concept-groups/${conceptGroupId}/concepts${searchParams}`;
        },
      }),
      getConceptGroupDetails: builder.query<
        ObjectResponse<ConceptGroup>,
        { conceptGroupId: string }
      >({
        query: ({ conceptGroupId }) => `/concept-groups/${conceptGroupId}`,
        providesTags: [CONCEPT_GROUP_TAG],
      }),
      // OR-6214: OMOP concept details by id, for the inline-concept chip on the
      // AV Definition tab's "Concept mappings" section. GET
      // /concept-mapping/concept/{conceptId}.
      getOmopConceptById: builder.query<ObjectResponse<OmopConcept>, { conceptId: number }>({
        query: ({ conceptId }) => `/concept-mapping/concept/${conceptId}`,
      }),
      postIsValidCreateConceptGroupName: builder.mutation<
        IsValidCreateConceptGroupNameResponse,
        { conceptGroupName: string; projectId?: string }
      >({
        query: ({ conceptGroupName, projectId }) => ({
          url: `/concept-groups/create/is-valid-name`,
          method: 'POST',
          body: { conceptGroupName, ...(projectId ? { projectId } : {}) },
        }),
      }),
      postIsValidCreateConceptGroupUpload: builder.mutation<
        ListResponse<IsValidCreateUploadResponse>,
        FormData
      >({
        query: (formData) => ({
          url: `/concept-groups/create/is-valid-upload`,
          method: 'POST',
          body: formData,
          formData: true,
        }),
      }),
      postCreateConceptGroup: builder.mutation<
        CreateConceptGroupResponse,
        {
          conceptGroupName: string;
          conceptIds: Array<string>;
          projectId?: string;
          filterKey?: string;
        }
      >({
        query: ({ conceptGroupName, conceptIds, projectId, filterKey }) => ({
          url: `/concept-groups`,
          method: 'POST',
          body: { conceptGroupName, conceptIds, projectId, filterKey },
        }),
        invalidatesTags: [CONCEPT_GROUP_TAG, CUSTOM_CHARACTERISTICS_CONCEPT_GROUP_TAG],
      }),
      // API for checking if concept group is in use
      // GET /v2/projects/concept-groups/{concept_group_id}/usage
      getConceptGroupUsage: builder.query<
        { data: { conceptGroupId: string; isUsed: boolean; usageCount: number } },
        { conceptGroupId: string }
      >({
        query: ({ conceptGroupId }) => `/concept-groups/${conceptGroupId}/usage`,
      }),
      // API for deleting concept group
      // DELETE /v2/projects/concept-groups/{concept_group_id}
      deleteConceptGroup: builder.mutation<void, { conceptGroupId: string }>({
        query: ({ conceptGroupId }) => ({
          url: `/concept-groups/${conceptGroupId}`,
          method: 'DELETE',
        }),
        invalidatesTags: [CONCEPT_GROUP_TAG, CUSTOM_CHARACTERISTICS_CONCEPT_GROUP_TAG],
      }),
      // Preview endpoints (concept_group_preview_mode)
      getPreviewConcepts: builder.query<
        ListResponse<IsValidCreateUploadResponse>,
        { previewId: string; searchParams?: string }
      >({
        query: ({ previewId, searchParams }) =>
          `/concept-groups/preview/${previewId}/concepts${
            searchParams ? `?${searchParams}` : '?page=1&pageSize=100'
          }`,
      }),
      getPreviewCreatedGroups: builder.query<
        Array<{ id: string; name: string }>,
        { previewId: string }
      >({
        query: ({ previewId }) => `/concept-groups/preview/${previewId}/created-groups`,
        providesTags: [CONCEPT_GROUP_TAG],
      }),
      postConfirmPreview: builder.mutation<
        CreateConceptGroupResponse,
        ConceptGroupPreviewConfirmRequest
      >({
        query: (body) => ({
          url: `/concept-groups/preview/${body.previewId}/confirm`,
          method: 'POST',
          body,
        }),
        invalidatesTags: [CONCEPT_GROUP_TAG, CUSTOM_CHARACTERISTICS_CONCEPT_GROUP_TAG],
      }),
    }),
  });

// export hooks to access/fetch data
export const {
  useGetLegacyConceptGroupsQuery,
  useGetConceptGroupsQuery,
  useGetConceptGroupsForCustomCharacteristicsQuery,
  usePostIsValidCreateConceptGroupNameMutation,
  usePostIsValidCreateConceptGroupUploadMutation,
  usePostCreateConceptGroupMutation,
  useGetConceptGroupDetailsQuery,
  useGetOmopConceptByIdQuery,
  useGetConceptGroupConceptsDetailsQuery,
  useLazyGetConceptGroupConceptsDetailsQuery,
  useGetConceptGroupUsageQuery,
  useDeleteConceptGroupMutation,
  useGetPreviewConceptsQuery,
  useLazyGetPreviewConceptsQuery,
  useGetPreviewCreatedGroupsQuery,
  usePostConfirmPreviewMutation,
} = conceptGroupsApiSlice;
